// # Tutor Bank Management Component for Admin
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../../auth/state';
import SharedHeader from '../../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../../components/DashboardFooter';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';
import { getSalaryDeductions } from '../../salaries/salary';
import './banco_tutors.css';

// # Utility function to get profile photo URL with proper fallback
const getProfilePhotoUrl = (userData) => {
  try {
    if (!userData?.photoURL) return '/icons/pfp.png';
    if (userData.photoURL === '') return '/icons/pfp.png';
    
    // # Check if it's a full URL (from Storage)
    if (userData.photoURL.startsWith('http')) {
      return userData.photoURL;
    }
    
    // # If it's just a filename, assume it's the default
    return '/icons/pfp.png';
  } catch (error) {
    console.error('Error getting profile photo:', error);
    return '/icons/pfp.png';
  }
};

// # Tutor Revenue Detail Component
const TutorRevenueDetail = ({ tutor, onBack }) => {
  // # Check if tutor data is available
  if (!tutor || !tutor.revenueData) {
    return (
      <div className="bnct_tutor_detail">
        <div className="bnct_tutor_detail-header">
          <button className="bnct_tutor_back-button" onClick={onBack}>
            Back to Tutor List
          </button>
          <h2>{tutor?.name || 'Tutor'} Revenue</h2>
        </div>
        <div className="bnct_tutor_detail-body">
          <p>No revenue data available for this tutor.</p>
        </div>
      </div>
    );
  }

  // # Format number with commas for thousands
  const formatNumber = (num) => {
    if (!num) return '0.00';
    return Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // # Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // # Get revenue data
  const { totalRevenue, netRevenue, paymentLogs, salaryDeductions, availableCash } = tutor.revenueData;
  
  // # Get profile photo with proper fallback
  const profilePhotoUrl = getProfilePhotoUrl(tutor);

  return (
    <div className="bnct_tutor_detail">
      <div className="bnct_tutor_detail-header">
        <button className="bnct_tutor_back-button" onClick={onBack}>
          Back to Tutor List
        </button>
        <h2>{tutor.name} Revenue</h2>
      </div>
      <div className="bnct_tutor_detail-profile">
        <div className="bnct_tutor_detail-image">
          <img src={profilePhotoUrl} alt={tutor.name} />
        </div>
        <div className="bnct_tutor_detail-info">
          <h3>{tutor.name}</h3>
          <p className="bnct_tutor_detail-email">{tutor.email}</p>
        </div>
      </div>
      <div className="bnct_tutor_detail-body">
        <div className="bnct_tutor_revenue-summary">
          <div className="bnct_tutor_revenue-stat">
            <h3>Total Revenue</h3>
            <p>E£ {formatNumber(totalRevenue)}</p>
          </div>
          <div className="bnct_tutor_revenue-stat">
            <h3>Net Revenue</h3>
            <p>E£ {formatNumber(netRevenue)}</p>
          </div>
          <div className="bnct_tutor_revenue-stat">
            <h3>Salary Deductions</h3>
            <p>E£ {formatNumber(salaryDeductions || 0)}</p>
          </div>
          <div className="bnct_tutor_revenue-stat">
            <h3>Available Cash</h3>
            <p>E£ {formatNumber(availableCash || 0)}</p>
          </div>
        </div>

        <div className="bnct_tutor_revenue-logs">
          <h3>Payment History</h3>
          {paymentLogs && paymentLogs.length > 0 ? (
            <div className="bnct_tutor_table-wrapper">
              <table className="bnct_tutor_revenue-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Product</th>
                    <th>User</th>
                    <th>Share %</th>
                    <th>Amount (EGP)</th>
                    <th>Total Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentLogs.map((log, index) => (
                    <tr key={index}>
                      <td>{formatDate(log.date)}</td>
                      <td>{log.product}</td>
                      <td>{log.user}</td>
                      <td>{log.percentage}%</td>
                      <td>E£ {formatNumber(log.amount)}</td>
                      <td>E£ {formatNumber(log.totalPayment || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No payment history available.</p>
          )}
        </div>

        {tutor.revenueData.salaryLogs && tutor.revenueData.salaryLogs.length > 0 && (
          <div className="bnct_tutor_salary-logs">
            <h3>Salary Payments</h3>
            <div className="bnct_tutor_table-wrapper">
              <table className="bnct_tutor_revenue-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount (EGP)</th>
                    <th>Notes</th>
                    <th>Recorded By</th>
                  </tr>
                </thead>
                <tbody>
                  {tutor.revenueData.salaryLogs.map((log, index) => (
                    <tr key={index}>
                      <td>{formatDate(log.timestamp)}</td>
                      <td>E£ {formatNumber(log.amount_egp)}</td>
                      <td>{log.notes}</td>
                      <td>{log.register_admin?.name || log.register_admin?.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const BancoTutors = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'

  // # Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Fetch tutors and their revenue data
  useEffect(() => {
    const fetchTutorsData = async () => {
      try {
        setLoading(true);
        
        // # 1. Get all financial data first to identify tutors
        const financialRef = collection(db, 'financial');
        const financialSnap = await getDocs(financialRef);
        
        // # Create a map to store unique tutor IDs found in payment data
        const tutorMap = new Map();
        
        // # Process financial data to find all tutors
        financialSnap.forEach(doc => {
          const yearMonthData = doc.data();
          
          // # Process payments if they exist
          if (yearMonthData.payments) {
            Object.entries(yearMonthData.payments).forEach(([paymentId, payment]) => {
              // # 1. Check the tutor field in the payment
              if (payment.tutor && payment.tutor.uid) {
                const tutorId = payment.tutor.uid;
                if (!tutorMap.has(tutorId)) {
                  tutorMap.set(tutorId, {
                    uid: tutorId,
                    name: payment.tutor.tutor_name || 'Unknown Tutor',
                    email: payment.tutor.email || '',
                    profileImage: '/icons/default_avatar.png',
                    revenueData: {
                      totalRevenue: 0,
                      netRevenue: 0,
                      salaryDeductions: 0,
                      availableCash: 0,
                      paymentLogs: [],
                      salaryLogs: []
                    }
                  });
                }
              }
              
              // # 2. Check entries in the shares field
              if (payment.shares) {
                Object.keys(payment.shares).forEach(shareId => {
                  // # Skip team members - they have specific IDs
                  const isTeamMember = ['ahmed_hadidy', 'nabil_mostafa', 'ahmed_ayman', 'ahmed_hussein', 'hossam_gomaa'].includes(shareId);
                  
                  // # If it's not a team member and appears in shares, it might be a tutor
                  if (!isTeamMember) {
                    // # Check if the payment is for tutor hosting
                    const isLikelyTutor = payment.pay?.product?.includes('tutor_hosting');
                    
                    if (isLikelyTutor && !tutorMap.has(shareId)) {
                      // # This is likely a tutor ID
                      tutorMap.set(shareId, {
                        uid: shareId,
                        name: 'Tutor ' + shareId.substring(0, 6), // Placeholder name, will be updated from users
                        email: '',
                        profileImage: '/icons/default_avatar.png',
                        revenueData: {
                          totalRevenue: 0,
                          netRevenue: 0,
                          salaryDeductions: 0,
                          availableCash: 0,
                          paymentLogs: [],
                          salaryLogs: []
                        }
                      });
                    }
                  }
                });
              }
            });
          }
        });
        
        // # 2. Get all users to get proper user information for tutors
        const usersSnapshot = await getDocs(collection(db, 'users'));
        
        // # Update tutor info from users collection
        usersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          const userId = doc.id;
          
          // # If we identified this user as a tutor in the financial data, update their info
          if (tutorMap.has(userId)) {
            const tutorInfo = tutorMap.get(userId);
            
            tutorMap.set(userId, {
              ...tutorInfo,
              name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || tutorInfo.name,
              email: userData.email || tutorInfo.email,
              profileImage: getProfilePhotoUrl(userData)
            });
          }
          
          // # Also add users with role='tutor' who might not have any payments yet
          if (userData.role === 'tutor' && !tutorMap.has(userId)) {
            tutorMap.set(userId, {
              uid: userId,
              name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Unknown Tutor',
              email: userData.email || '',
              profileImage: getProfilePhotoUrl(userData),
              revenueData: {
                totalRevenue: 0,
                netRevenue: 0,
                salaryDeductions: 0,
                availableCash: 0,
                paymentLogs: [],
                salaryLogs: []
              }
            });
          }
        });
        
        // # 3. Get salary deductions
        const salaryDeductions = await getSalaryDeductions();
        
        // # 4. Process financial data again to calculate revenue for each tutor
        financialSnap.forEach(doc => {
          const yearMonthData = doc.data();
          
          // # Process payments if they exist
          if (yearMonthData.payments) {
            Object.entries(yearMonthData.payments).forEach(([paymentId, payment]) => {
              // # Get tutor ID from the payment
              const tutorId = payment.tutor?.uid || null;
              
              // # Process shares if they exist
              if (payment.shares) {
                Object.entries(payment.shares).forEach(([shareId, shareData]) => {
                  // # Check if this share belongs to a tutor
                  if (tutorMap.has(shareId)) {
                    const tutor = tutorMap.get(shareId);
                    
                    // # Calculate amounts
                    const netShareEgp = parseFloat(shareData.net_share_egp) || 0;
                    const totalShareEgp = parseFloat(shareData.total_share_egp) || 0;
                    const percentage = parseFloat(shareData.percentage_of_net) || 0;
                    
                    // # Update total and net revenue
                    tutor.revenueData.totalRevenue += totalShareEgp;
                    tutor.revenueData.netRevenue += netShareEgp;
                    
                    // # Add to payment logs
                    if (netShareEgp > 0) {
                      tutor.revenueData.paymentLogs.push({
                        id: paymentId,
                        amount: netShareEgp,
                        totalAmount: totalShareEgp,
                        date: payment.metadata?.created_at || new Date().toISOString(),
                        product: payment.pay?.product || 'Unknown',
                        user: payment.user?.name || 'Unknown User',
                        percentage: percentage,
                        totalPayment: payment.pay?.egp_total || 0
                      });
                    }
                    
                    // # Update the tutor in the map
                    tutorMap.set(shareId, tutor);
                  }
                });
              }
              
              // # Special case: If tutor is specified but no shares entry (legacy data)
              if (tutorId && tutorMap.has(tutorId) && payment.pay && (!payment.shares || !payment.shares[tutorId])) {
                const tutor = tutorMap.get(tutorId);
                
                // # For tutor hosting, tutors typically get 50% of net
                const netAmount = parseFloat(payment.pay.egp_net) || 0;
                const netShareEgp = netAmount * 0.5; // 50% share for tutor hosting
                const totalShareEgp = netShareEgp * 1.05; // Approximate before budget
                const percentage = 50;
                
                // # Update total and net revenue
                tutor.revenueData.totalRevenue += totalShareEgp;
                tutor.revenueData.netRevenue += netShareEgp;
                
                // # Add to payment logs
                if (netShareEgp > 0) {
                  tutor.revenueData.paymentLogs.push({
                    id: paymentId,
                    amount: netShareEgp,
                    totalAmount: totalShareEgp,
                    date: payment.metadata?.created_at || new Date().toISOString(),
                    product: payment.pay?.product || 'Unknown',
                    user: payment.user?.name || 'Unknown User',
                    percentage: percentage,
                    totalPayment: payment.pay?.egp_total || 0
                  });
                }
                
                // # Update the tutor in the map
                tutorMap.set(tutorId, tutor);
              }
            });
          }
        });
        
        // # 5. Add salary deductions for each tutor
        salaryDeductions.forEach(salary => {
          const tutorId = salary.receiver?.uid;
          if (tutorId && tutorMap.has(tutorId) && salary.receiver?.reciever_type === 'tutor') {
            const tutor = tutorMap.get(tutorId);
            const salaryAmount = parseFloat(salary.amount_egp) || 0;
            
            // # Update salary deductions
            tutor.revenueData.salaryDeductions += salaryAmount;
            
            // # Add to salary logs
            tutor.revenueData.salaryLogs.push({
              id: salary.id,
              timestamp: salary.timestamp || salary.metadata?.created_at,
              amount_egp: salaryAmount,
              notes: salary.notes || salary.metadata?.notes || '',
              register_admin: salary.register_admin
            });
            
            // # Update the tutor in the map
            tutorMap.set(tutorId, tutor);
          }
        });
        
        // # 6. Calculate available cash for each tutor
        tutorMap.forEach((tutor, id) => {
          tutor.revenueData.availableCash = tutor.revenueData.netRevenue - tutor.revenueData.salaryDeductions;
          tutorMap.set(id, tutor);
        });
        
        // # Convert the map to an array and sort by revenue
        const tutorsArray = Array.from(tutorMap.values());
        
        // # Sort tutors by net revenue (highest first)
        tutorsArray.sort((a, b) => b.revenueData.netRevenue - a.revenueData.netRevenue);
        
        // # Sort payment logs by date (newest first) for each tutor
        tutorsArray.forEach(tutor => {
          tutor.revenueData.paymentLogs.sort((a, b) => new Date(b.date) - new Date(a.date));
          
          // # Sort salary logs by date (newest first)
          if (tutor.revenueData.salaryLogs) {
            tutor.revenueData.salaryLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          }
        });
        
        // # Update state
        setTutors(tutorsArray);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching tutors data:', error);
        setLoading(false);
      }
    };
    
    fetchTutorsData();
  }, []);

  // # Handle tutor card click
  const handleTutorClick = (tutor) => {
    setSelectedTutor(tutor);
    setViewMode('detail');
    window.scrollTo(0, 0); // Scroll to top when viewing details
  };

  // # Handle back to list
  const handleBackToList = () => {
    setViewMode('list');
    setSelectedTutor(null);
  };

  // # Format money number
  const formatMoney = (amount) => {
    return 'E£ ' + amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className="bnct_tutor_page">
      <SharedHeader />
      
      <main className="bnct_tutor_content">
        {viewMode === 'list' ? (
          <>
            <div className="bnct_tutor_header">
              <h1>Tutor Bank Accounts</h1>
              <div className="bnct_tutor_nav-buttons">
                <button 
                  className="bnct_tutor_back-button"
                  onClick={() => navigate('/admin/financial/bank')}
                >
                  Back to Bank Management
                </button>
              </div>
            </div>

            {loading ? (
              <div className="bnct_tutor_loading">Loading tutor financial data...</div>
            ) : tutors.length === 0 ? (
              <div className="bnct_tutor_no-data">No tutors found</div>
            ) : (
              <div className="bnct_tutor_grid">
                {tutors.map((tutor) => (
                  <div 
                    key={tutor.uid} 
                    className="bnct_tutor_card"
                    onClick={() => handleTutorClick(tutor)}
                  >
                    <div className="bnct_tutor_image">
                      <img src={tutor.profileImage} alt={tutor.name} />
                    </div>
                    <div className="bnct_tutor_info">
                      <h3>{tutor.name}</h3>
                      <p className="bnct_tutor_email">{tutor.email}</p>
                      <div className="bnct_tutor_financials">
                        <div className="bnct_tutor_revenue">
                          <p className="bnct_tutor_revenue-label">Net Revenue</p>
                          <p className="bnct_tutor_revenue-value">{formatMoney(tutor.revenueData.netRevenue)}</p>
                        </div>
                        <div className="bnct_tutor_payments">
                          <p className="bnct_tutor_payments-label">Payments</p>
                          <p className="bnct_tutor_payments-value">{tutor.revenueData.paymentLogs.length}</p>
                        </div>
                      </div>
                      <div className="bnct_tutor_financials">
                        <div className="bnct_tutor_salary">
                          <p className="bnct_tutor_salary-label">Salary Paid</p>
                          <p className="bnct_tutor_salary-value">{formatMoney(tutor.revenueData.salaryDeductions)}</p>
                        </div>
                        <div className="bnct_tutor_balance">
                          <p className="bnct_tutor_balance-label">Balance</p>
                          <p className="bnct_tutor_balance-value">{formatMoney(tutor.revenueData.availableCash)}</p>
                        </div>
                      </div>
                      <button 
                        className="bnct_tutor_view-details"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTutorClick(tutor);
                        }}
                      >
                        View Revenue Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <TutorRevenueDetail 
            tutor={selectedTutor}
            onBack={handleBackToList}
          />
        )}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default BancoTutors;
